import { NoSsr } from "@mui/base";
import { useAsync, useMountEffect } from "@react-hookz/web";
import { useAtomValue } from "jotai";
import type { FunctionComponent } from "react";
import { Suspense } from "react";
import { graphql, useFragment } from "react-relay";

import { accountAtom } from "~/lib/account";
import { fetchAiEngineData } from "~/lib/ai-engine/apis";
import { AiEngineDataType } from "~/lib/ai-engine/enums";
import type { topPicksWidgetContent$key } from "~/queries/__generated__/topPicksWidgetContent.graphql";

import type { Props as TopPicksWidgetDisplayProps } from "./top-picks-widget-display";
import { TopPicksWidgetDisplay } from "./top-picks-widget-display";

export type Props = Omit<TopPicksWidgetDisplayProps, "aiData"> & {
  reference: topPicksWidgetContent$key;
};

export const TopPicksWidget: FunctionComponent<Props> = ({
  reference: reference_,
  ...extraProps
}) => {
  const { user } = useAtomValue(accountAtom);
  const content = useFragment(
    graphql`
      fragment topPicksWidgetContent on Article {
        entityId
      }
    `,
    reference_,
  );

  const [{ result }, { execute }] = useAsync(() =>
    fetchAiEngineData<AiEngineDataType.TopPicks>({
      entityId: content.entityId,
      type: AiEngineDataType.TopPicks,
      userUuid: user?.uuid,
    }),
  );

  useMountEffect(() => {
    void execute();
  });

  return result?.data ? (
    <NoSsr>
      <Suspense>
        <TopPicksWidgetDisplay aiData={result?.data} {...extraProps} />
      </Suspense>
    </NoSsr>
  ) : null;
};

TopPicksWidget.displayName = "TopPicksWidget";
